import { memo, useCallback, useEffect, useMemo } from 'react';

import compose from 'lodash.flowright';
import Path from 'path-to-regexp';
import PropType from 'prop-types';
import { withRouter } from 'react-router-dom';

import { Button } from '@/shadcn/button';
import { cn } from '@/shadcn/lib/utils';

import { uploadConfig, uploadFile } from '@/client/media';
import { withLeavingAlertContext } from '@/components/beforeLeavingAlert';
import { FormWriter } from '@/components/forms/components/form/writer';
import { Footer } from '@/components/layouts';
import { getStatePathPrefill } from '@/components/newLink/statePathPrefill';
import {
  getUserInfoDisplayConfig,
  manageLocalStorage,
  sectionsLocalStorage
} from '@/components/ui/utils/localStorage';
import { Widget } from '@/components/ui/widget';
import { ROUTE_WORKFLOW_OVERVIEW } from '@/constant/routes';
import { LocalStorageContext } from '@/contexts';
import { WorkflowContext } from '@/utils/workflowContext';

const FORM_ID = 'formId';

export const Form = memo(props => {
  const {
    match,
    history,
    location,
    action,
    workflowContext,
    traces,
    disabled,
    toggleSignDialog,
    formData = null,
    setFormData,
    setCleanupFn,
    setAskBeforeLeaving,
    setLeavingCallback,
    cacheKey,
    toggleAnswerGroupDialog = () => {},
    answer = false,
    // Scalable Batch Actions (STMN-3359): when provided, a valid submit runs a
    // server-side batch (server signs via impersonateEmail) instead of opening
    // the client-side SignLinkDialog. Receives the collected formData.
    onBatchSubmit = null,
    // Size of the batch. In batch mode only one representative trace is
    // hydrated, so `traces.length` no longer describes the selection.
    traceCount = null
  } = props;

  // localStorage key for backup cache
  const cachedUpdatesKey = useMemo(
    () => `${cacheKey}${window.location}`,
    [location]
  );

  // Cached data
  const cachedFormData = useMemo(() => {
    const localStorageValue = localStorage.getItem(cachedUpdatesKey);
    return localStorageValue ? JSON.parse(localStorageValue) : null;
  }, [cachedUpdatesKey]);

  // at first mount if there is cached data:
  useEffect(() => {
    if (cachedFormData) {
      // call setFormData with it
      setFormData(cachedFormData);
      // set leaving alert condition to true
      setAskBeforeLeaving?.(true);
    }
  }, [cachedFormData]);

  // set cleanup function (triggered after form submission) and leaving callback (triggered after user confirmed leaving)
  useEffect(() => {
    if (cachedUpdatesKey) {
      const cleanupLocalStorage = () => {
        localStorage.removeItem(cachedUpdatesKey);
        setFormData(null);
      };
      cleanupLocalStorage();
      setLeavingCallback?.(cleanupLocalStorage);
      setCleanupFn(cleanupLocalStorage);
    }
  }, [cachedUpdatesKey]);

  const handleFormChange = useCallback(
    ({ formData: data }) => {
      // If data is null or undefined, ensure we don't proceed with further operations
      if (!data) {
        localStorage.removeItem(cachedUpdatesKey);
        setFormData(null);
        setAskBeforeLeaving?.(false);
        return;
      }

      localStorage.setItem(cachedUpdatesKey, JSON.stringify(data));
      setFormData(data);

      // ! to optimize this, we could just always set askBeforeLeaving to true

      const values = Object.values(data);
      // checks if all values are undefined or empty
      const valuesAreAllUndefinedOrEmpty = values.every(value => {
        if (typeof value === 'string' && value.trim() === '') {
          return true; // value is an empty string
        }
        if (Array.isArray(value) && value.length === 0) {
          return true; // value is an empty array
        }
        if (value === undefined) {
          return true; // value is undefined
        }
        return false; // value is not empty or undefined
      });
      // sets asksBeforeLeaving to false when all values are empty and/or undefined
      // and to true if at least one is not empty or defined
      if (valuesAreAllUndefinedOrEmpty) {
        setAskBeforeLeaving?.(false);
      } else {
        setAskBeforeLeaving?.(true);
      }
    },
    [cachedUpdatesKey]
  );

  const handleLocalStorage = useCallback(
    ({ index, isCollapsed }) => {
      const [trace] = traces;
      return manageLocalStorage(trace, { index, isCollapsed });
    },
    [traces]
  );

  const goToWorkflowOverview = () => {
    history.push(
      Path.compile(ROUTE_WORKFLOW_OVERVIEW)({ id: match.params.wfid })
    );
  };

  const goBack = () => {
    if (location.state && location.state.from) {
      history.push(location.state.from);
      return;
    }
    // By default go back to workflow overview
    goToWorkflowOverview();
  };

  // ! This should NOT be defined here, but directly in the FileUploadField component
  const handleUploadFile = async (
    file,
    onSuccess,
    onError,
    onProgress,
    disableEncryption
  ) => {
    uploadFile(file, onSuccess, onError, onProgress, disableEncryption);
  };

  const [trace] = traces;

  const schema = action.form
    ? action.form.schema
    : {
        type: 'object',
        title: action.title,
        description: action.description
      };
  const uiSchema = action.form ? action.form.uiSchema : {};

  // Hide trace info for batch update
  const isBatchForm = (traceCount ?? traces.length) > 1;

  // !!! This should be inside the FileUploadField component
  const { MAX_FILE_SIZE, FILE_EXTENSION_WHITELIST_MAP } = uploadConfig;

  // !! who cares?
  const wfUserDisplayConfig = useMemo(
    () => getUserInfoDisplayConfig(trace?.workflow?.rowId) || undefined,
    [trace?.workflow?.rowId]
  );

  const localStorageContext = useMemo(
    () => ({
      userInfoConfig:
        (wfUserDisplayConfig && wfUserDisplayConfig[trace?.rowId]) ||
        sectionsLocalStorage(
          trace?.workflow?.config?.info?.view?.sections || []
        ),
      setLocalStorage: handleLocalStorage
    }),
    [trace?.rowId]
  );

  useEffect(() => {
    const prefill = getStatePathPrefill(uiSchema, trace);
    if (Object.keys(prefill).length) {
      setFormData(prevFormData => ({ ...prevFormData, ...prefill }));
    }
  }, []);

  const onSubmit = e => {
    if (onBatchSubmit) {
      // Batch mode: the server builds + signs each link, so we skip the
      // signature dialog entirely and hand the validated formData back.
      onBatchSubmit(formData);
      return;
    }
    if (answer) {
      toggleAnswerGroupDialog(e);
    } else {
      toggleSignDialog(e);
    }
  };

  return (
    <div
      className={cn('w-full', {
        'mt-subnavbar mr-[2px] flex h-[calc(100vh-var(--spacing-full-navbar))] flex-wrap items-start overflow-y-auto bg-background pt-6':
          !answer,
        'mt-5 flex items-start overflow-y-auto border-border border-t pt-[10px]':
          answer
      })}
    >
      <div
        className={cn('relative mx-auto gap-5', {
          'mb-6 grid w-full max-w-(--breakpoint-2xl) grid-cols-1 px-3 md:px-10 lg:grid-cols-5 lg:px-20':
            !answer,
          'flex w-full flex-wrap': answer
        })}
      >
        <div
          className={cn('box-border h-fit grow bg-card', {
            'col-span-1 rounded-md border border-border p-5 lg:col-span-3':
              !answer,
            'h-full rounded-sm': answer
          })}
          data-cy="formjs-writer"
          data-has-traceinfo={
            trace && !isBatchForm && !!trace?.workflow?.config.info
          }
        >
          <FormWriter
            description={action.description}
            formData={formData || cachedFormData}
            id={FORM_ID}
            onChange={handleFormChange}
            onSubmit={onSubmit}
            schema={schema}
            title={action.title}
            traceContext={trace}
            uiSchema={uiSchema}
            uploadConfig={{
              maxFileSize: MAX_FILE_SIZE,
              fileExtensionWhitelistMap: FILE_EXTENSION_WHITELIST_MAP
            }}
            uploadFile={handleUploadFile}
            workflowContext={workflowContext}
          >
            <div className="mt-12 flex w-full justify-between gap-4">
              <div>
                {!answer && (
                  <Button
                    data-cy="cancel-button"
                    onClick={() => goBack()}
                    type="button"
                    variant="outline"
                  >
                    Cancel
                  </Button>
                )}
              </div>
              <Button
                data-cy="submit-button"
                disabled={disabled}
                form-id={FORM_ID}
                type="submit"
                variant="default"
              >
                {answer ? 'Answer' : 'Submit'}
              </Button>
            </div>
          </FormWriter>
        </div>
        {trace && !isBatchForm && trace?.workflow?.config.info && (
          <div
            className="col-span-1 grow rounded-sm bg-inherit lg:col-span-2"
            data-cy="trace-info"
          >
            <WorkflowContext.Provider value={workflowContext}>
              <LocalStorageContext.Provider value={localStorageContext}>
                <Widget
                  data={trace.state}
                  widget={trace?.workflow?.config.info}
                />
              </LocalStorageContext.Provider>
            </WorkflowContext.Provider>
          </div>
        )}
      </div>
      {!answer && <Footer className="relative w-full self-end" />}
    </div>
  );
});

Form.propTypes = {
  history: PropType.object.isRequired,
  location: PropType.object.isRequired,
  match: PropType.object.isRequired,
  action: PropType.object.isRequired,
  workflowContext: PropType.object.isRequired,
  traces: PropType.arrayOf(PropType.object).isRequired,
  disabled: PropType.bool.isRequired,
  toggleSignDialog: PropType.func.isRequired,
  formData: PropType.object,
  setFormData: PropType.func.isRequired,
  setCleanupFn: PropType.func.isRequired,
  setAskBeforeLeaving: PropType.func.isRequired,
  setLeavingCallback: PropType.func.isRequired,
  cacheKey: PropType.string.isRequired,
  toggleAnswerGroupDialog: PropType.func,
  answer: PropType.bool,
  onBatchSubmit: PropType.func,
  traceCount: PropType.number
};

export default compose(withRouter, withLeavingAlertContext)(Form);
